/**
 * useFlowChatControllers — the non-visual controller logic shared by V1
 * (`FlowChatPanel.tsx`) and V2 (`shell-next/chat/FlowChatPanelV2.tsx`)
 * (bead terra-design-v2-chat-yub.1).
 *
 * Extracted wholesale from `FlowChatPanel.tsx`'s pre-split `FlowChatPanel`
 * (list view) and `FlowChatSessionView` (per-session drill-in) bodies — see
 * that file's own header comment for the multi-session store's shape and
 * invariants. This file owns ONLY what both markup trees need to actually
 * DO (create/select/resume a session, derive display values); it owns no
 * dropdown-open/header-chrome UI state (coding-standards.md: "V2 components
 * are markup + local UI state only") and no stream effects
 * (`SessionStreamHost` in `FlowChatSessionsProvider.tsx` stays the sole
 * stream owner and sole pending-first-message flusher).
 *
 * `startOrResumeSessionForAccount` and `getFlowChatAccountIdFromPathname`
 * also live here (moved from `FlowChatPanel.tsx`) so `FlowChatPanel.tsx` can
 * re-export the former without owning its implementation — this is what
 * keeps the import graph acyclic: `FlowChatPanel` -> `FlowChatPanelV2` ->
 * this file, never back into `FlowChatPanel` (`shell-next/account/
 * useOpenAccountChat.ts` (#1757) imports `startOrResumeSessionForAccount`
 * from `FlowChatPanel.tsx`'s re-export, unchanged).
 */
import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  useKeystoneChatPanelController,
  type UseKeystoneChatPanelControllerReturn,
} from '../../account-view/keystone/chat-panel-core';
import { useKeystoneConversationMeta } from '../../account-view/keystone/useKeystoneConversationMeta';
import { useFlowAccount } from '../account/hooks';
import {
  useFlowChatSessions,
  useFlowChatSessionList,
  type FlowChatSessionSummary,
} from './FlowChatSessionsProvider';
import { useFlowChatSessionAdapter } from './useFlowChatSessionAdapter';
import { copyFlowChatLink } from './flowChatLink';

/**
 * `useKeystoneChatPanelController`'s `setConversationId` arg is inert for
 * the store-backed adapter path (nothing downstream of it writes to a
 * `chatConversationId` slot anymore — session identity lives in the store's
 * `activeSessionId`). Module-level so its reference never changes across
 * renders (avoids needlessly re-creating the controller's internal, unused
 * `handleSend`/`handleNew`/`handleExampleClick` callbacks).
 */
const noopSetConversationId = (_id: string | null) => {};

/**
 * Extract the `:accountId` route segment from any of the three account-page
 * shapes the dock is mounted alongside: `/accounts/:accountId(/:accountTab)`
 * (AppRouter.tsx V4 routes), the Command Center drill-down mounted under both
 * `/tools/command-center/*` and the `/flow/*` unified shell
 * (mini-apps/command-center/index.tsx), and the Flow shell's own
 * `/flow/accounts/:accountId`.
 *
 * Consulted only at CLICK time (inside the two controller hooks' click
 * handlers below), never from a navigation-triggered effect — nothing acts
 * on it until the rep explicitly clicks (D-C, explicit-action rule).
 *
 * Returns the DECODED segment (`%20` etc. resolved) or `null` when the
 * pathname has no `/accounts/:accountId` segment at all.
 */
function getFlowChatAccountIdFromPathname(pathname: string): string | null {
  const match = /\/accounts\/([^/?#]+)/.exec(pathname);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    // Malformed percent-encoding — fall back to the raw segment rather than throwing.
    return match[1];
  }
}

/**
 * Shared "start-or-resume a chat for {accountId}" primitive (2026-08-20 QA
 * follow-up). Both `useFlowChatListController`'s `handleNewChatClick` and
 * `useFlowChatSessionController`'s `handleInSessionNewChatClick` route
 * through this one function so the "does a live session for this account
 * already exist? resume it; else create one" rule can never drift between
 * the two entry points.
 */
export function startOrResumeSessionForAccount(
  accountId: string,
  accountName: string,
  sessions: FlowChatSessionSummary[],
  createSession: (accountId: string, accountName: string) => string,
  switchToSession: (sessionId: string) => void,
): void {
  const existing = sessions.find((s) => s.accountId === accountId);
  const sessionId = existing ? existing.sessionId : createSession(accountId, accountName);
  switchToSession(sessionId);
}

// useFlowChatListController — the session-LIST landing view's controller.

export interface FlowChatListController {
  sessions: FlowChatSessionSummary[];
  activeSessionId: string | null;
  view: 'list' | 'session';
  openSession: (sessionId: string) => void;
  backToList: () => void;
  /** The page account underneath the dock, re-derived every render (see
   *  `getFlowChatAccountIdFromPathname`'s doc comment) — markup reads this
   *  to decide whether "New chat" direct-starts or offers the picker. */
  pageAccountId: string | null;
  /** "+" picker (list view only) — a GlobalSearch `mode="accounts"` popover.
   *  Genuinely shared (not V1-specific chrome): both V1 and V2 need the
   *  SAME "is the account picker open" fact, unlike the session view's
   *  header-dropdown tri-state (kept local to each markup tree). */
  newSessionPickerOpen: boolean;
  closeNewSessionPicker: () => void;
  /** Page-account direct-start via `startOrResumeSessionForAccount`; else
   *  toggles `newSessionPickerOpen`. */
  handleNewChatClick: () => void;
  pickNewSessionAccount: (accountId: string) => void;
  selectPersistedConversation: (
    conversationId: string,
    accountId: string,
    accountName: string | null,
  ) => void;
  stopSession: (sessionId: string) => void;
}

export function useFlowChatListController(): FlowChatListController {
  const { activeSessionId, setActiveSessionId, createSession, stopSession, focusOrCreateSessionForConversation } =
    useFlowChatSessions();
  const sessions = useFlowChatSessionList();
  const location = useLocation();
  const pageAccountId = getFlowChatAccountIdFromPathname(location.pathname);
  const pageAccountQuery = useFlowAccount(pageAccountId);

  // Derived, never mirrored — the ONLY source of truth for list-vs-session
  // is the store's `activeSessionId` (FlowChatPanel.tsx's file header,
  // "List-vs-session view is DERIVED, never mirrored").
  const view: 'list' | 'session' = activeSessionId ? 'session' : 'list';

  const openSession = useCallback(
    (sessionId: string) => {
      setActiveSessionId(sessionId);
    },
    [setActiveSessionId],
  );
  const backToList = useCallback(() => setActiveSessionId(null), [setActiveSessionId]);

  const [newSessionPickerOpen, setNewSessionPickerOpen] = useState(false);
  const closeNewSessionPicker = useCallback(() => setNewSessionPickerOpen(false), []);

  const pickNewSessionAccount = useCallback(
    (accountId: string) => {
      // `createSession` already sets the store's `activeSessionId` — `view`
      // derives from it, so no separate `openSession` call is needed.
      createSession(accountId, '');
      setNewSessionPickerOpen(false);
    },
    [createSession],
  );

  const handleNewChatClick = useCallback(() => {
    if (pageAccountId) {
      startOrResumeSessionForAccount(
        pageAccountId,
        pageAccountQuery.data?.account?.name ?? '',
        sessions,
        createSession,
        setActiveSessionId,
      );
      return;
    }
    setNewSessionPickerOpen((prev) => !prev);
  }, [pageAccountId, sessions, pageAccountQuery.data, createSession, setActiveSessionId]);

  const selectPersistedConversation = useCallback(
    (conversationId: string, pickedAccountId: string, pickedAccountName: string | null) => {
      const focusedId = focusOrCreateSessionForConversation(pickedAccountId, conversationId, pickedAccountName ?? '');
      setActiveSessionId(focusedId);
    },
    [focusOrCreateSessionForConversation, setActiveSessionId],
  );

  return {
    sessions,
    activeSessionId,
    view,
    openSession,
    backToList,
    pageAccountId,
    newSessionPickerOpen,
    closeNewSessionPicker,
    handleNewChatClick,
    pickNewSessionAccount,
    selectPersistedConversation,
    stopSession,
  };
}

// useFlowChatSessionController — the per-session drill-in's controller.

export interface FlowChatSessionController {
  entry: FlowChatSessionSummary | null;
  session: ReturnType<typeof useFlowChatSessionAdapter>['session'];
  externalStream: ReturnType<typeof useFlowChatSessionAdapter>['externalStream'];
  draftText: string;
  setDraftText: (text: string) => void;
  isRunning: boolean;
  stopThisSession: () => void;
  accountId: string;
  accountLabel: string | undefined;
  conversationId: string | null;
  chatTitle: string;
  /** The persona name persisted on an EXISTING conversation (reload chip) —
   *  `useKeystoneConversationMeta(conversationId).personaName`, the same
   *  read V1's `KeystoneChatSurface` makes (`chat-panel-core.tsx:382`).
   *  `null` pre-chat or while loading. */
  loadedPersonaName: string | null;
  controller: UseKeystoneChatPanelControllerReturn;
  /** See `FlowChatListController.pageAccountId`'s doc comment. */
  pageAccountId: string | null;
  selectConversation: (id: string) => void;
  selectDraftSession: (draftSessionId: string) => void;
  startNewForAccount: () => void;
  startNewForDifferentAccount: (pickedAccountId: string) => void;
  /** Page-account direct-start only — no-ops when there is no page account
   *  underneath (the "offer this session's account, or search a different
   *  one" fallback is V1-specific header chrome, kept local to
   *  `FlowChatSessionView`; see that component for the wrapping). */
  handleInSessionNewChatClick: () => void;
  handleCopyLink: () => void;
  /** Un-stops this session and queues `text` as its pending first message
   *  (D-H, "the send is the resume") — bound to this hook's `sessionId`. */
  resumeWithMessage: (text: string) => void;
}

export function useFlowChatSessionController(sessionId: string): FlowChatSessionController {
  const { createSession, setActiveSessionId, stopSession, resumeWithMessage: resumeSessionWithMessage, focusOrCreateSessionForConversation } =
    useFlowChatSessions();
  const sessions = useFlowChatSessionList();
  const { entry, session, externalStream, draftText, setDraftText } = useFlowChatSessionAdapter(sessionId);

  // Same page-account derivation the list controller uses — re-derived
  // locally here (rather than threaded through) since `useLocation`/
  // `useFlowAccount` are cheap, side-effect-free reads. Powers the
  // in-session "+" button's page-account-first rule — see
  // `startOrResumeSessionForAccount`'s doc comment for the "why."
  const location = useLocation();
  const pageAccountId = getFlowChatAccountIdFromPathname(location.pathname);
  const pageAccountQuery = useFlowAccount(pageAccountId);

  // Derived at render, never mirrored — the SAME derivation
  // `FlowChatSessionList`'s badge uses, so the composer's Stop control and
  // the list row's Stop control never disagree about whether this session
  // is Running.
  const isRunning = entry ? entry.createStatus === 'creating' || Boolean(externalStream?.isRunning) : false;
  const stopThisSession = useCallback(() => stopSession(sessionId), [stopSession, sessionId]);

  const accountId = entry?.accountId ?? '';
  const accountQuery = useFlowAccount(entry?.accountName ? null : accountId || null);
  const accountLabel = entry?.accountName || accountQuery.data?.account?.name || accountId || undefined;

  const conversationId = entry?.conversationId ?? null;
  const { title: conversationTitle, personaName: loadedPersonaName } = useKeystoneConversationMeta(conversationId);
  const chatTitle = conversationId ? (conversationTitle ?? 'New conversation') : 'New chat';

  const controller = useKeystoneChatPanelController({
    accountId,
    conversationId,
    setConversationId: noopSetConversationId,
    externalSession: session,
  });

  const selectConversation = useCallback(
    (id: string) => {
      const focusedId = focusOrCreateSessionForConversation(accountId, id, accountLabel);
      setActiveSessionId(focusedId);
    },
    [focusOrCreateSessionForConversation, accountId, accountLabel, setActiveSessionId],
  );
  const selectDraftSession = useCallback(
    (draftSessionId: string) => {
      setActiveSessionId(draftSessionId);
    },
    [setActiveSessionId],
  );
  const startNewForAccount = useCallback(() => {
    const newId = createSession(accountId, accountLabel ?? '');
    setActiveSessionId(newId);
  }, [createSession, accountId, accountLabel, setActiveSessionId]);
  const startNewForDifferentAccount = useCallback(
    (pickedAccountId: string) => {
      const newId = createSession(pickedAccountId, '');
      setActiveSessionId(newId);
    },
    [createSession, setActiveSessionId],
  );
  const handleInSessionNewChatClick = useCallback(() => {
    if (!pageAccountId) return;
    startOrResumeSessionForAccount(
      pageAccountId,
      pageAccountQuery.data?.account?.name ?? '',
      sessions,
      createSession,
      setActiveSessionId,
    );
  }, [pageAccountId, pageAccountQuery.data, sessions, createSession, setActiveSessionId]);

  const handleCopyLink = useCallback(() => {
    if (!conversationId) return;
    void copyFlowChatLink(conversationId, accountId, {
      onSuccess: () => toast.success('Chat link copied'),
      onError: () => toast.error('Could not copy link'),
    });
  }, [conversationId, accountId]);

  const resumeWithMessage = useCallback(
    (text: string) => resumeSessionWithMessage(sessionId, text),
    [resumeSessionWithMessage, sessionId],
  );

  return {
    entry,
    session,
    externalStream,
    draftText,
    setDraftText,
    isRunning,
    stopThisSession,
    accountId,
    accountLabel,
    conversationId,
    chatTitle,
    loadedPersonaName,
    controller,
    pageAccountId,
    selectConversation,
    selectDraftSession,
    startNewForAccount,
    startNewForDifferentAccount,
    handleInSessionNewChatClick,
    handleCopyLink,
    resumeWithMessage,
  };
}
